"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@templates/design-system";
import { cn } from "@newxsystems/shared";
import type { ITootAccordionProps } from "./TootAccordion.props";

const sizeClasses = {
  indicator: "h-9 w-9",
  panel: "px-5 pb-5 pt-4 text-[0.95rem] leading-7",
  root: "text-base",
  trigger: "min-h-14 px-5 py-4",
} as const;

export const TootAccordion = ({
  className,
  direction,
  items,
  label,
  mode,
}: ITootAccordionProps) => {
  const preparedItems = items ?? [];
  const defaultOpenValues = preparedItems
    .map((item, index) => (item.defaultOpen ? String(index) : undefined))
    .filter((value): value is string => value !== undefined);
  const allowMultipleOpen = mode === "multi";
  const accordionKey = `${mode ?? ""}:${preparedItems.length}:${defaultOpenValues.join("|")}`;

  const accordionItems = preparedItems.map((item, index) => {
    const itemId = String(index);

    return (
      <AccordionItem
        className={cn(
          "relative overflow-hidden border-t border-[color-mix(in_oklab,var(--color-toot-grid-strong)_68%,transparent)] transition-[background-color,border-color] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] data-[state=closed]:bg-toot-surface-raised/50 data-[state=closed]:hover:bg-[color-mix(in_oklab,var(--color-toot-orange)_7%,transparent)] data-[state=open]:bg-[radial-gradient(circle_at_0%_0%,rgba(255,77,46,0.12),transparent_15rem),rgba(255,255,255,0.025)] motion-reduce:transition-none",
          index === 0 && "border-t-0",
        )}
        key={itemId}
        value={itemId}
      >
        <AccordionTrigger
          className={cn(
            "group/toot-accordion-trigger w-full gap-4 text-left transition-[background-color,color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-toot-surface-terminal)] motion-reduce:transition-none [&>svg:last-child]:hidden",
            sizeClasses.trigger,
          )}
        >
          <span className="flex min-w-0 items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-[0.45rem] hidden h-2 w-2 shrink-0 rounded-full border border-[var(--color-toot-orange)] bg-[var(--color-toot-black)] shadow-[0_0_1rem_rgba(255,138,31,0.48)] group-data-[state=open]/toot-accordion-trigger:bg-[var(--color-toot-red)] group-data-[state=open]/toot-accordion-trigger:shadow-[0_0_1.4rem_rgba(255,77,46,0.58)] sm:block"
            />
            <span className="min-w-0">
              <span className="block text-balance text-[1.05rem] font-black leading-tight tracking-[-0.035em] text-[color-mix(in_oklab,var(--color-toot-foreground)_94%,transparent)] sm:text-[1.16rem]">
                {item.heading}
              </span>
            </span>
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "relative flex shrink-0 items-center justify-center rounded-full border border-[color-mix(in_oklab,var(--color-toot-orange)_46%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_8%,transparent)] text-[var(--color-toot-orange)] transition-[background-color,border-color,color,transform,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/toot-accordion-trigger:border-[var(--color-toot-orange)] group-hover/toot-accordion-trigger:bg-[color-mix(in_oklab,var(--color-toot-orange)_14%,transparent)] group-data-[state=open]/toot-accordion-trigger:border-[var(--color-toot-red)] group-data-[state=open]/toot-accordion-trigger:bg-[var(--color-toot-red)] group-data-[state=open]/toot-accordion-trigger:text-[var(--color-primary-foreground)] group-data-[state=open]/toot-accordion-trigger:shadow-[0_0_1.4rem_rgba(255,77,46,0.24)] motion-reduce:transition-none",
              sizeClasses.indicator,
            )}
          >
            <span className="absolute h-[2px] w-3.5 rounded-full bg-current" />
            <span className="absolute h-3.5 w-[2px] rounded-full bg-current transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-data-[state=open]/toot-accordion-trigger:scale-y-0 group-data-[state=open]/toot-accordion-trigger:opacity-0 motion-reduce:transition-none" />
          </span>
        </AccordionTrigger>
        <AccordionContent
          className={cn(
            "overflow-hidden border-t border-[color-mix(in_oklab,var(--color-toot-grid-strong)_52%,transparent)] text-[color-mix(in_oklab,var(--color-toot-foreground)_78%,transparent)] data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
            sizeClasses.panel,
          )}
        >
          <div className="max-w-3xl text-pretty [overflow-wrap:anywhere] [&_a]:text-[var(--color-toot-orange)] [&_a]:underline-offset-4 [&_a:hover]:underline [&_p]:m-0 [&_p+*]:mt-4">
            {item.content}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  });

  if (!accordionItems.length) {
    return null;
  }

  return (
    <div
      aria-label={label}
      className={cn(
        "relative overflow-hidden rounded-[1.35rem] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_70%,transparent)] bg-[var(--color-toot-surface-terminal)] shadow-[0_1.5rem_4rem_rgba(0,0,0,0.24)]",
        sizeClasses.root,
        className,
      )}
      dir={direction ?? undefined}
      data-toot-accordion-direction={direction ?? undefined}
      data-toot-accordion-mode={mode ?? undefined}
      role={label ? "region" : undefined}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.07)_1px,transparent_1px)] bg-[size:1.75rem_1.75rem] opacity-36 [mask-image:linear-gradient(180deg,black,transparent_88%)]"
      />
      {mode === "single" ? (
        <Accordion
          className="relative grid"
          collapsible
          defaultValue={defaultOpenValues[0]}
          key={accordionKey}
          type="single"
        >
          {accordionItems}
        </Accordion>
      ) : (
        <Accordion
          className="relative grid"
          defaultValue={allowMultipleOpen ? defaultOpenValues : defaultOpenValues.slice(0, 1)}
          key={accordionKey}
          type="multiple"
        >
          {accordionItems}
        </Accordion>
      )}
    </div>
  );
};

TootAccordion.displayName = "Interactive::TootAccordion";
